import { useState, useRef, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { formatMoney } from '@/lib/utils'
import { useAuth } from '@/features/auth/AuthProvider'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
}

// ─── Queries (shared cache with Dashboard) ───────────────────────────────────

type DashboardApiData = {
  ventas: { ventas_hoy: string; total_ventas_hoy: string }
  clientes: { con_deuda: string; deuda_total: string }
  alertas: Array<{ id: string; nombre: string; stock_minimo: number; stock_total: string }>
  movimientos: Array<{ tipo: string }>
}

type AlertaRow = { id: string; nombre: string; marca: string | null; stock_minimo: number; stockTotal: number }

function useDashboardCtx() {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get<DashboardApiData>('/api/dashboard/stats'),
    staleTime: 30_000,
  })
}

function useCajaCtx() {
  return useQuery({
    queryKey: ['dashboard-caja'],
    queryFn: async () => {
      try {
        return await api.get<{ id: string; fecha_apertura: string }>('/api/caja/sesion-activa')
      } catch {
        return null
      }
    },
    staleTime: 60_000,
  })
}

// ─── System prompt ────────────────────────────────────────────────────────────

function buildSystemPrompt(
  ventas: { count: number; totalMonto: number; mayoristas: number; ticket: number } | undefined,
  caja: { id?: string; fecha_apertura: string } | null | undefined,
  alertas: AlertaRow[],
  cc: { conDeuda: number; total: number; top3?: { nombre: string; saldo_actual: number }[] } | undefined,
): string {
  const ahora = new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
    + ' · ' + new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })

  const ventasStr = ventas
    ? `${ventas.count} operaciones · $${formatMoney(ventas.totalMonto)} facturado · ticket $${formatMoney(ventas.ticket)} · ${ventas.mayoristas} mayoristas`
    : 'sin datos'

  const cajaStr = caja
    ? `ABIERTA desde las ${new Date(caja.fecha_apertura).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`
    : 'CERRADA'

  const sinStock = alertas.filter(a => a.stockTotal === 0)
  const bajMin = alertas.filter(a => a.stockTotal > 0)
  const alertasStr = alertas.length === 0
    ? 'Sin alertas — todo el stock en orden.'
    : [
        sinStock.length > 0 ? `SIN STOCK (${sinStock.length}): ${sinStock.slice(0, 5).map(a => a.nombre).join(', ')}` : '',
        bajMin.length > 0 ? `BAJO MÍNIMO (${bajMin.length}): ${bajMin.slice(0, 5).map(a => `${a.nombre} (${a.stockTotal}/${a.stock_minimo})`).join(', ')}` : '',
      ].filter(Boolean).join(' | ')

  const ccStr = cc
    ? `${cc.conDeuda} clientes · total $${formatMoney(cc.total)}${(cc.top3 ?? []).length > 0 ? ` · mayores: ${(cc.top3 ?? []).map(c => `${c.nombre} $${formatMoney(c.saldo_actual)}`).join(', ')}` : ''}`
    : 'sin datos'

  return `Sos el asistente de MayoExpress, un sistema POS para almacén/kiosco argentino.

Tu rol: ayudar al operador a entender ventas, stock, caja y cuentas corrientes. Respondé en español argentino informal (usá "vos"). Sé conciso. Usá $ para montos. No inventés datos: si algo no está en el contexto, decilo y sugerí dónde encontrarlo en la app.

== ESTADO DEL NEGOCIO (${ahora}) ==
VENTAS HOY: ${ventasStr}
CAJA: ${cajaStr}
ALERTAS DE STOCK: ${alertasStr}
CUENTAS CORRIENTES: ${ccStr}`
}

// ─── Quick suggestions ────────────────────────────────────────────────────────

const SUGERENCIAS = [
  '¿Cómo van las ventas de hoy?',
  '¿Qué productos necesito reponer?',
  '¿Quién me debe más?',
  'Dame un resumen del negocio',
]

// ─── Markdown renderer ────────────────────────────────────────────────────────

function parseInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i}>{part.slice(1, -1)}</em>
    if (part.startsWith('`') && part.endsWith('`'))
      return (
        <code key={i} style={{
          fontFamily: 'var(--me-mono)', fontSize: '0.88em',
          background: 'var(--me-surface-2)', padding: '1px 5px', borderRadius: 4,
        }}>
          {part.slice(1, -1)}
        </code>
      )
    return part
  })
}

function SimpleMarkdown({ text }: { text: string }) {
  if (!text) return <span style={{ color: 'var(--me-text-4)' }}>...</span>

  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  const listBuffer: string[] = []

  function flushList() {
    if (!listBuffer.length) return
    elements.push(
      <ul key={`ul-${elements.length}`} style={{ margin: '4px 0 8px', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {listBuffer.map((item, i) => (
          <li key={i}>{parseInline(item)}</li>
        ))}
      </ul>
    )
    listBuffer.length = 0
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (/^[-•*]\s/.test(line)) {
      listBuffer.push(line.replace(/^[-•*]\s/, ''))
      continue
    }

    flushList()

    if (line.startsWith('### ')) {
      elements.push(<div key={i} style={{ fontWeight: 700, fontSize: 13, marginTop: elements.length ? 10 : 0, marginBottom: 2, color: 'var(--me-text)' }}>{parseInline(line.slice(4))}</div>)
      continue
    }
    if (line.startsWith('## ')) {
      elements.push(<div key={i} style={{ fontWeight: 700, fontSize: 14, marginTop: elements.length ? 12 : 0, marginBottom: 4, color: 'var(--me-text)' }}>{parseInline(line.slice(3))}</div>)
      continue
    }
    if (line.trim() === '') {
      if (elements.length) elements.push(<div key={`sp-${i}`} style={{ height: 6 }} />)
      continue
    }

    elements.push(<div key={i}>{parseInline(line)}</div>)
  }

  flushList()

  return <div style={{ lineHeight: 1.65 }}>{elements}</div>
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AsistentePage() {
  const { profile } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const dashQuery = useDashboardCtx()
  const cajaQuery = useCajaCtx()

  const ctxReady = !dashQuery.isLoading && !cajaQuery.isLoading

  const ventasData = dashQuery.data ? {
    count:      parseInt(dashQuery.data.ventas.ventas_hoy),
    totalMonto: parseFloat(dashQuery.data.ventas.total_ventas_hoy),
    mayoristas: (dashQuery.data.movimientos ?? []).filter(m => m.tipo === 'mayorista').length,
    ticket: parseInt(dashQuery.data.ventas.ventas_hoy) > 0
      ? parseFloat(dashQuery.data.ventas.total_ventas_hoy) / parseInt(dashQuery.data.ventas.ventas_hoy)
      : 0,
  } : undefined

  const alertasData: AlertaRow[] = (dashQuery.data?.alertas ?? []).map(a => ({
    id: a.id, nombre: a.nombre, marca: null,
    stock_minimo: Number(a.stock_minimo),
    stockTotal: parseFloat(a.stock_total),
  }))

  const ccData = dashQuery.data ? {
    conDeuda: parseInt(dashQuery.data.clientes.con_deuda),
    total:    parseFloat(dashQuery.data.clientes.deuda_total),
  } : undefined

  const systemPrompt = useMemo(() => {
    if (!ctxReady) return ''
    return buildSystemPrompt(ventasData, cajaQuery.data, alertasData, ccData)
  }, [ctxReady, ventasData, cajaQuery.data, alertasData, ccData])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function handleSend(text: string) {
    const trimmed = text.trim()
    if (!trimmed || isTyping) return

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: trimmed }
    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setInput('')
    resetTextareaHeight()
    setIsTyping(true)

    const assistantId = `a-${Date.now()}`
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '' }])

    try {
      const apiMessages = [
        { role: 'system', content: systemPrompt || buildSystemPrompt(ventasData, cajaQuery.data, alertasData, ccData) },
        ...nextMessages.map(m => ({ role: m.role, content: m.content })),
      ]

      const res = await api.stream('/api/asistente/chat', { messages: apiMessages })

      if (!res.ok) throw new Error(`Groq ${res.status}`)

      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buf = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (payload === '[DONE]') continue
          try {
            const delta = JSON.parse(payload).choices?.[0]?.delta?.content
            if (delta) {
              setMessages(prev => {
                const last = prev[prev.length - 1]
                return last?.id === assistantId
                  ? [...prev.slice(0, -1), { ...last, content: last.content + delta }]
                  : prev
              })
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages(prev => {
        const last = prev[prev.length - 1]
        return last?.id === assistantId
          ? [...prev.slice(0, -1), { ...last, content: 'No pude contactar al asistente. Verificá tu conexión e intentá de nuevo.' }]
          : prev
      })
    } finally {
      setIsTyping(false)
      inputRef.current?.focus()
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(input)
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value)
    // auto-resize
    e.target.style.height = 'auto'
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
  }

  function resetTextareaHeight() {
    if (inputRef.current) inputRef.current.style.height = 'auto'
  }

  function handleClear() {
    setMessages([])
    setInput('')
    resetTextareaHeight()
    inputRef.current?.focus()
  }

  const nombre = profile?.nombre?.split(' ')[0] ?? 'vos'
  const hasMessages = messages.length > 0

  // Typing indicator: the last message is the streaming one (empty or filling in)
  const streamingMsgId = isTyping ? messages[messages.length - 1]?.id : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 'var(--me-space-5)' }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Inteligencia artificial</div>
          <h1 className="me-page-title">Asistente</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--me-space-2)', paddingBottom: 4 }}>
          {!ctxReady && (
            <span style={{ fontSize: 11, color: 'var(--me-text-4)', fontFamily: 'var(--me-mono)' }}>
              cargando contexto...
            </span>
          )}
          {hasMessages && (
            <button
              onClick={handleClear}
              style={{
                padding: '6px 12px', border: 'none',
                borderRadius: 8, background: 'var(--me-surface-3)',
                color: 'var(--me-text-2)', fontFamily: 'var(--me-sans)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                transition: 'all var(--me-dur-short)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
              onMouseOver={e => {
                ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--me-acid)'
                ;(e.currentTarget as HTMLButtonElement).style.color = 'var(--me-acid-ink)'
              }}
              onMouseOut={e => {
                ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--me-surface-3)'
                ;(e.currentTarget as HTMLButtonElement).style.color = 'var(--me-text-2)'
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
              </svg>
              Nueva conversación
            </button>
          )}
          <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', color: 'var(--me-text-4)',
            textTransform: 'uppercase', background: 'var(--me-surface-3)', borderRadius: 6,
            padding: '3px 8px', fontFamily: 'var(--me-mono)',
          }}>
            llama-3.1-8b
          </span>
          <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', color: '#4a5800',
            textTransform: 'uppercase', background: 'var(--me-acid-soft)', borderRadius: 6,
            padding: '3px 8px', fontFamily: 'var(--me-mono)',
          }}>
            Groq
          </span>
        </div>
      </div>

      {/* ── Chat panel ──────────────────────────────────────────────────── */}
      <div className="me-card" style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        padding: 0, overflow: 'hidden', minHeight: 0,
      }}>

        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: 'var(--me-space-5)',
          display: 'flex', flexDirection: 'column', gap: 'var(--me-space-4)',
        }}>
          {!hasMessages ? (
            <WelcomeCard
              nombre={nombre}
              ventas={ventasData}
              caja={cajaQuery.data}
              alertas={alertasData}
              cc={ccData}
              ctxReady={ctxReady}
            />
          ) : (
            messages.map(m => (
              <MessageBubble
                key={m.id}
                message={m}
                isStreaming={m.id === streamingMsgId}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {!hasMessages && (
          <div style={{ padding: '0 var(--me-space-5) var(--me-space-3)', display: 'flex', gap: 'var(--me-space-2)', flexWrap: 'wrap' }}>
            {SUGERENCIAS.map(s => (
              <button
                key={s}
                onClick={() => handleSend(s)}
                disabled={isTyping || !ctxReady}
                style={{
                  padding: '6px 12px', border: '1px solid var(--me-line)',
                  borderRadius: 20, background: 'var(--me-surface-2)',
                  color: 'var(--me-text-2)', fontFamily: 'var(--me-sans)',
                  fontSize: 'var(--me-text-sm)', cursor: 'pointer',
                  transition: 'all var(--me-dur-short)',
                  opacity: !ctxReady ? 0.45 : 1,
                }}
                onMouseOver={e => { if (ctxReady) (e.currentTarget as HTMLButtonElement).style.background = 'var(--me-surface-3)' }}
                onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--me-surface-2)' }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{
          borderTop: '1px solid var(--me-line-2)',
          padding: 'var(--me-space-3) var(--me-space-4)',
          display: 'flex', gap: 'var(--me-space-2)', alignItems: 'flex-end',
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={
              !ctxReady ? 'Cargando contexto del negocio...'
              : 'Preguntá algo... (Enter para enviar, Shift+Enter para nueva línea)'
            }
            disabled={isTyping || !ctxReady}
            rows={1}
            style={{
              flex: 1, resize: 'none', border: '1px solid var(--me-line-2)',
              borderRadius: 10, padding: '10px 14px',
              fontFamily: 'var(--me-sans)', fontSize: 'var(--me-text-base)',
              color: 'var(--me-text)', background: 'var(--me-surface)',
              outline: 'none', lineHeight: 1.5,
              transition: 'border-color var(--me-dur-short)',
              overflowY: 'auto',
              opacity: !ctxReady ? 0.55 : 1,
            }}
            onFocus={e => { e.currentTarget.style.borderColor = 'var(--me-acid)' }}
            onBlur={e => { e.currentTarget.style.borderColor = 'var(--me-line-2)' }}
          />
          <button
            onClick={() => handleSend(input)}
            disabled={!input.trim() || isTyping || !ctxReady}
            title="Enviar (Enter)"
            style={{
              width: 40, height: 40, borderRadius: 10, border: 'none', flexShrink: 0,
              background: input.trim() && ctxReady && !isTyping
                ? 'var(--me-acid)' : 'var(--me-surface-3)',
              cursor: input.trim() && ctxReady && !isTyping ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background var(--me-dur-short)',
            }}
          >
            <SendIcon active={!!(input.trim() && ctxReady && !isTyping)} />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function MessageBubble({ message, isStreaming }: { message: Message; isStreaming?: boolean }) {
  const isUser = message.role === 'user'

  if (isUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{
          maxWidth: '72%', padding: '10px 14px',
          background: 'var(--me-acid)', color: 'var(--me-acid-ink)',
          borderRadius: '14px 14px 4px 14px',
          fontFamily: 'var(--me-sans)', fontSize: 'var(--me-text-base)', lineHeight: 1.55,
        }}>
          {message.content}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--me-space-2)', alignItems: 'flex-start' }}>
      <AIAvatar />
      <div style={{
        maxWidth: '80%', padding: '10px 14px',
        background: 'var(--me-surface)', border: '1px solid var(--me-line-2)',
        borderRadius: '4px 14px 14px 14px',
        fontFamily: 'var(--me-sans)', fontSize: 'var(--me-text-base)',
        color: 'var(--me-text)',
      }}>
        {message.content
          ? <SimpleMarkdown text={message.content} />
          : isStreaming
          ? <TypingDots />
          : <span style={{ color: 'var(--me-text-4)' }}>...</span>
        }
        {isStreaming && message.content && (
          <span style={{
            display: 'inline-block', width: 2, height: '0.9em',
            background: 'var(--me-text-3)', marginLeft: 2,
            animation: 'me-cursor-blink 0.9s step-end infinite',
            verticalAlign: 'text-bottom',
          }} />
        )}
      </div>
      <style>{`@keyframes me-cursor-blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
    </div>
  )
}

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '2px 0' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 6, height: 6, borderRadius: '50%', background: 'var(--me-text-4)',
          animation: `me-dot-pulse 1.2s ease-in-out ${i * 0.18}s infinite`,
        }} />
      ))}
      <style>{`@keyframes me-dot-pulse{0%,60%,100%{transform:translateY(0);opacity:.35}30%{transform:translateY(-4px);opacity:1}}`}</style>
    </div>
  )
}

function AIAvatar() {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
      background: 'var(--me-acid)', color: 'var(--me-acid-ink)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontFamily: 'var(--me-display)', fontWeight: 700, letterSpacing: '-0.03em',
    }}>
      IA
    </div>
  )
}

function WelcomeCard({ nombre, ventas, caja, alertas, cc, ctxReady }: {
  nombre: string
  ventas: { count: number; totalMonto: number; mayoristas: number } | undefined
  caja: { fecha_apertura: string } | null | undefined
  alertas: AlertaRow[]
  cc: { conDeuda: number; total: number } | undefined
  ctxReady: boolean
}) {
  const totalAlertas = alertas.length
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--me-space-5)', paddingTop: 'var(--me-space-4)' }}>
      <div>
        <div style={{
          fontSize: 'var(--me-text-xl)', fontFamily: 'var(--me-display)',
          fontVariationSettings: '"opsz" 24, "wght" 700',
          letterSpacing: '-0.025em', color: 'var(--me-text)', marginBottom: 6,
        }}>
          Hola, {nombre}
        </div>
        <div style={{ fontSize: 'var(--me-text-base)', color: 'var(--me-text-3)', lineHeight: 1.6 }}>
          Soy el asistente de MayoExpress. Preguntame sobre ventas, stock, caja o cuentas corrientes.
        </div>
      </div>
      {ctxReady ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--me-space-3)' }}>
          <CtxCard label="Ventas hoy" value={ventas ? `$${formatMoney(ventas.totalMonto)}` : '—'} detail={ventas ? `${ventas.count} operaciones` : ''} ok={!!ventas?.count} />
          <CtxCard label="Caja" value={caja !== undefined ? (caja ? 'Abierta' : 'Cerrada') : '—'} detail={caja ? `Desde las ${new Date(caja.fecha_apertura).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}` : 'Sin sesión activa'} ok={!!caja} />
          <CtxCard label="Alertas de stock" value={String(totalAlertas)} detail={totalAlertas === 0 ? 'Todo en orden' : `${alertas.filter(a => a.stockTotal === 0).length} sin stock`} ok={totalAlertas === 0} urgent={totalAlertas > 0} />
          <CtxCard label="Cuentas corrientes" value={cc ? `$${formatMoney(cc.total)}` : '—'} detail={cc ? `${cc.conDeuda} clientes con saldo` : ''} ok={!cc?.total} urgent={!!cc?.total} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--me-space-3)' }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ height: 72, borderRadius: 10, background: 'var(--me-surface-2)', opacity: 1 - i * 0.15 }} />
          ))}
        </div>
      )}
    </div>
  )
}

function CtxCard({ label, value, detail, ok, urgent }: {
  label: string; value: string; detail: string; ok?: boolean; urgent?: boolean
}) {
  const valueColor = urgent ? 'var(--me-bad)' : ok ? 'var(--me-ok)' : 'var(--me-text)'
  return (
    <div style={{
      padding: 'var(--me-space-4)', borderRadius: 10,
      border: `1px solid ${urgent ? 'rgba(239,68,68,0.2)' : 'var(--me-line-2)'}`,
      background: urgent ? 'var(--me-bad-soft)' : 'var(--me-surface)',
    }}>
      <div style={{ fontSize: 'var(--me-text-xs)', color: 'var(--me-text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 20, fontWeight: 700, color: valueColor, lineHeight: 1, marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--me-text-3)' }}>{detail}</div>
    </div>
  )
}

function SendIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? 'var(--me-acid-ink)' : 'var(--me-text-4)'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  )
}
